import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import SiteLayout from '@/components/site/SiteLayout';
import { CtaPanel } from '@/components/site/PageSections';
import { getAbsoluteUrl, SITE_URL } from '@/lib/seoRoutes';
import {
  getNewsArticleBySlug,
  newsArticles,
  type NewsArticle,
} from '@/data/news';

type Props = {
  article: NewsArticle;
};

const NewsDetailPage = ({
  article,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const showStrategicPartnershipGraphic =
    article.heroVariant === 'strategic-partnership';

  return (
    <SiteLayout
      title={`${article.title} | DocMoonlight News`}
      description={article.excerpt}
      keywords={['DocMoonlight news', 'contrast supervision', 'radiology news']}
    >
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'NewsArticle',
              headline: article.title,
              description: article.excerpt,
              url: getAbsoluteUrl(`/news/${article.slug}`),
              datePublished: article.date,
              dateModified: article.date,
              author: {
                '@type': 'Organization',
                name: article.authors,
              },
              publisher: {
                '@type': 'Organization',
                name: 'DocMoonlight',
                url: SITE_URL,
                logo: {
                  '@type': 'ImageObject',
                  url: getAbsoluteUrl('/images/logo.svg'),
                },
              },
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': getAbsoluteUrl(`/news/${article.slug}`),
              },
            }),
          }}
        />
      </Head>
      <section className='px-4 pb-8 pt-10 sm:px-6 lg:px-8 lg:pt-14'>
        <div className='mx-auto max-w-7xl'>
          <div className='mb-5'>
            <Link
              href='/news'
              className='inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-950'
            >
              Back to news
            </Link>
          </div>

          <article className='overflow-hidden rounded-[2.25rem] border border-slate-200 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.08)]'>
            <div className='border-b border-slate-200 bg-[linear-gradient(180deg,#f8fbff_0%,#eef6ff_100%)] px-6 py-8 md:px-10 md:py-10'>
              <div className='text-sm font-bold uppercase tracking-[0.2em] text-sky-700'>
                Featured release
              </div>
              <h1 className='mt-4 max-w-5xl text-2xl font-black leading-tight tracking-tight text-slate-950 md:text-4xl'>
                {article.title}
              </h1>
              <p className='mt-5 text-sm font-semibold text-slate-500 md:text-base'>
                {article.authors} | {article.date}
              </p>
            </div>

            <div className='px-6 py-8 md:px-10 md:py-10'>
              <div className='mx-auto max-w-4xl space-y-6 text-lg leading-8 text-slate-700'>
                {showStrategicPartnershipGraphic && (
                  <div className='overflow-hidden rounded-[2rem] bg-[#05070F] px-6 py-8 text-white shadow-[0_24px_70px_rgba(15,23,42,0.14)] md:px-10 md:py-10'>
                    <div className='text-center text-xs font-bold uppercase tracking-[0.55em] text-white/95 md:text-sm'>
                      Strategic Partnership
                    </div>

                    <div className='mx-auto mt-8 max-w-3xl'>
                      <div className='rounded-[1.4rem] bg-white px-6 py-9 md:px-10 md:py-10'>
                        <div className='mx-auto flex w-fit items-center justify-center rounded-[1rem] bg-black px-6 py-4 md:px-8 md:py-5'>
                          <Image
                            src='/images/contrast-connect.svg'
                            alt='ContrastConnect'
                            width={240}
                            height={64}
                            sizes='(max-width: 767px) 192px, 240px'
                            className='h-10 w-auto md:h-12'
                          />
                        </div>
                      </div>

                      <div className='relative mx-auto -mt-3 flex h-20 w-20 items-center justify-center rounded-full bg-black ring-8 ring-[#05070F]'>
                        <span className='text-4xl font-light leading-none text-white'>
                          ×
                        </span>
                      </div>

                      <div className='-mt-3 rounded-[1.4rem] bg-white px-6 py-8 md:px-10 md:py-9'>
                        <div className='flex items-center justify-center'>
                          <Image
                            src='/images/logo.svg'
                            alt='DocMoonlight'
                            width={260}
                            height={96}
                            sizes='(max-width: 767px) 220px, 260px'
                            className='h-20 w-auto md:h-24'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {article.blocks.map((block, index) => {
                  if (block.type === 'quote') {
                    const quoteClass =
                      block.tone === 'sky'
                        ? 'border-sky-100 bg-sky-50'
                        : 'border-slate-200 bg-slate-50';

                    return (
                      <blockquote
                        key={`${block.type}-${index}`}
                        className={`rounded-[1.75rem] border px-5 py-5 text-slate-800 shadow-sm ${quoteClass}`}
                      >
                        <p>{block.content}</p>
                      </blockquote>
                    );
                  }

                  return <p key={`${block.type}-${index}`}>{block.content}</p>;
                })}

                {article.contactEmail && (
                  <div className='rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm'>
                    <div className='text-sm font-bold uppercase tracking-[0.18em] text-sky-700'>
                      Media contact
                    </div>
                    <p className='mt-3 text-base leading-7 text-slate-700'>
                      For more information, please reach out to{' '}
                      {article.contactName || 'our team'} at{' '}
                      <a
                        href={`mailto:${article.contactEmail}`}
                        className='font-semibold text-sky-700 underline-offset-4 hover:underline'
                      >
                        {article.contactEmail}
                      </a>
                      .
                    </p>
                  </div>
                )}
              </div>
            </div>
          </article>
        </div>
      </section>

      <CtaPanel
        title='Need Contrast Coverage Support?'
        body='DocMoonlight can help imaging centers build onsite, virtual, or hybrid supervision models that fit operational and regulatory needs.'
        action={{
          href: '/contact#contact-form',
          label: 'Talk to DocMoonlight',
        }}
      />
    </SiteLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: newsArticles.map((article) => ({
      params: { slug: article.slug },
    })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const slug = typeof params?.slug === 'string' ? params.slug : '';
  const article = getNewsArticleBySlug(slug);

  if (!article) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      article,
    },
  };
};

export default NewsDetailPage;
